import React, { useState, useCallback } from 'react';
import {
	Box,
	Typography,
	Tabs,
	Tab,
	IconButton,
	Card,
	CardContent,
	Button,
	TextField,
	Select,
	MenuItem,
	FormControl,
	InputLabel,
	Grid,
	Stack,
	Chip,
	Alert,
	CircularProgress,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	List,
	ListItem,
	ListItemText,
	Divider,
	Paper,
	Avatar,
	Skeleton
} from '@mui/material';
import {
	Close as CloseIcon,
	Save as SaveIcon,
	Image as ImageIcon,
	Download as DownloadIcon,
	Comment as CommentIcon,
	Code as CodeIcon,
	Language as LanguageIcon,
	Assignment as AssignmentIcon
} from '@mui/icons-material';
import useSWR, { mutate } from 'swr';
import { fetcher } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';

// Add: status transition map mirroring backend and helpers
const STATUS_LABELS: Record<string, string> = {
	UNRESOLVED: 'Unresolved',
	TRIAGED: 'Triaged',
	IN_PROGRESS: 'In Progress',
	DONE: 'Done',
	WONTFIX: "Won't Fix",
	DUPLICATE: 'Duplicate',
	NEEDS_INFO: 'Needs Info'
};

const TRANSITIONS: Record<string, string[]> = {
	UNRESOLVED: ['TRIAGED', 'NEEDS_INFO', 'WONTFIX', 'DUPLICATE', 'IN_PROGRESS'],
	TRIAGED: ['IN_PROGRESS', 'NEEDS_INFO', 'WONTFIX', 'DUPLICATE'],
	IN_PROGRESS: ['DONE', 'NEEDS_INFO', 'WONTFIX'],
	NEEDS_INFO: ['TRIAGED', 'IN_PROGRESS', 'DONE', 'WONTFIX', 'DUPLICATE'],
	DONE: [],
	WONTFIX: [],
	DUPLICATE: []
};

const TERMINAL_STATUSES = new Set(['DONE', 'WONTFIX', 'DUPLICATE']);

function getAllowedStatusOptions(currentStatus: string): string[] {
	const next = TRANSITIONS[currentStatus] || [];
	// Include current status to allow leaving it unchanged
	return Array.from(new Set([currentStatus, ...next]));
}

// Safely normalize steps that may be stored as array, JSON string, or CSV/newline text
function normalizeSteps(steps: unknown): string[] {
	if (Array.isArray(steps)) {
		return steps.map(String).map(s => s.trim()).filter(Boolean);
	}
	if (typeof steps === 'string') {
		const trimmed = steps.trim();
		if (!trimmed) return [];
		try {
			const parsed = JSON.parse(trimmed);
			if (Array.isArray(parsed)) {
				return parsed.map(String).map(s => s.trim()).filter(Boolean);
			}
		} catch {
			// Fall through to delimiter-based parsing
		}
		// Support newline or comma separated fallback
		return trimmed
			.split(/[\r\n]+|,/)
			.map(s => s.trim())
			.filter(Boolean);
	}
	return [];
}

interface FeedbackDetailsProps {
	feedbackId: string;
	onClose: () => void;
	onUpdate: () => void;
}

interface TabPanelProps {
	children?: React.ReactNode;
	index: number;
	value: number;
}

function TabPanel(props: TabPanelProps) {
	const { children, value, index, ...other } = props;
	return (
		<div
			role="tabpanel"
			hidden={value !== index}
			id={`feedback-tabpanel-${index}`}
			aria-labelledby={`feedback-tab-${index}`}
			{...other}
		>
			{value === index && <Box sx={{ py: 3 }}>{children}</Box>}
		</div>
	);
}

export const FeedbackDetails: React.FC<FeedbackDetailsProps> = ({ 
	feedbackId, 
	onClose, 
	onUpdate 
}) => {
	const { user, currentBusiness } = useAuth();
	const [activeTab, setActiveTab] = useState(0);
	const [isEditing, setIsEditing] = useState(false);
	const [editValues, setEditValues] = useState<any>({});
	const [isSaving, setIsSaving] = useState(false);
	const [newComment, setNewComment] = useState('');
	const [isAddingComment, setIsAddingComment] = useState(false);
	const [imageDialog, setImageDialog] = useState<{ open: boolean; src: string; title: string }>({
		open: false,
		src: '',
		title: ''
	});

	const { data: feedback, error, isLoading } = useSWR(
		`/admin/beta-feedback/${feedbackId}`,
		fetcher
	);

	const handleTabChange = useCallback((event: React.SyntheticEvent, newValue: number) => {
		setActiveTab(newValue);
	}, []);

	const handleEditToggle = useCallback(() => {
		if (isEditing) {
			// Cancel editing
			setEditValues({});
			setIsEditing(false);
		} else {
			// Start editing - populate with current values
			setEditValues({
				status: feedback?.status || '',
				severity: feedback?.severity || '',
				category: feedback?.category || '',
				assigned_to: feedback?.assigned_to || '',
				resolution_summary: feedback?.resolution_summary || '',
				external_issue_url: feedback?.external_issue_url || '',
				duplicate_of: feedback?.duplicate_of || ''
			});
			setIsEditing(true);
		}
	}, [isEditing, feedback]);

	const handleSave = useCallback(async () => {
		if (!feedbackId) return;
		
		setIsSaving(true);
		try {
			const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/+$/, '');

			// Build minimal payload: omit empty strings; map empty assigned_to/duplicate_of to null
			const allowedFields = [
				'status',
				'severity',
				'category',
				'assigned_to',
				'resolution_summary',
				'external_issue_url',
				'duplicate_of'
			] as const;

			const payload: Record<string, any> = {};
			for (const field of allowedFields) {
				if (Object.prototype.hasOwnProperty.call(editValues, field)) {
					const value = (editValues as any)[field];
					if (field === 'assigned_to' || field === 'duplicate_of') {
						// Backend schema allows null; use null when cleared in UI
						if (value === '') payload[field] = null;
						else if (value != null) payload[field] = value;
					} else {
						// Omit empty strings to avoid Zod enum/url validations failing
						if (value !== '' && value != null) payload[field] = value;
					}
				}
			}

			// UI-side validation to prevent server 400s on invalid transitions/requirements
			const currentStatus: string | undefined = (editValues as any).current_status || undefined;
			// If we didn't track current explicitly, infer from feedback state below when available
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			const selectedStatus: any = (editValues as any).status;

			// Require resolution_summary for terminal transitions
			if (selectedStatus && TERMINAL_STATUSES.has(String(selectedStatus))) {
				const summary = String((editValues as any).resolution_summary || '').trim();
				if (!summary) {
					throw new Error(`Resolution summary is required when setting status to ${selectedStatus}`);
				}
			}
			// Require duplicate_of for DUPLICATE
			if (selectedStatus === 'DUPLICATE') {
				const dupOf = (editValues as any).duplicate_of;
				if (!dupOf) {
					throw new Error('Duplicate Of is required when setting status to DUPLICATE');
				}
			}

			const response = await fetch(`${API_BASE_URL}/admin/beta-feedback/${feedbackId}`, {
				method: 'PATCH',
				headers: {
					'Content-Type': 'application/json'
				},
				credentials: 'include',
				body: JSON.stringify(payload)
			});

			if (!response.ok) {
				let message = 'Failed to update feedback';
				try {
					const errorData = await response.json();
					message = errorData.error?.message || message;
				} catch {
					try {
						const text = await response.text();
						if (text) message = text;
					} catch {}
				}
				throw new Error(message);
			}

			// Refresh the data
			mutate(`/admin/beta-feedback/${feedbackId}`);
			onUpdate();
			setIsEditing(false);
			setEditValues({});
		} catch (err: any) {
			console.error('Failed to save:', err);
			alert(`Failed to save: ${err.message}`);
		} finally {
			setIsSaving(false);
		}
	}, [feedbackId, editValues, currentBusiness?.id, onUpdate]);

	const handleAddComment = useCallback(async () => {
		if (!newComment.trim() || !feedbackId) return;
		
		setIsAddingComment(true);
		try {
			const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/+$/, '');
			const response = await fetch(`${API_BASE_URL}/admin/beta-feedback/${feedbackId}/comment`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json'
				},
				credentials: 'include',
				body: JSON.stringify({ body: newComment })
			});

			if (!response.ok) {
				const errorData = await response.json();
				throw new Error(errorData.error?.message || 'Failed to add comment');
			}

			// Refresh the data
			mutate(`/admin/beta-feedback/${feedbackId}`);
			setNewComment('');
		} catch (err: any) {
			console.error('Failed to add comment:', err);
			alert(`Failed to add comment: ${err.message}`);
		} finally {
			setIsAddingComment(false);
		}
	}, [newComment, feedbackId, currentBusiness?.id]);

	const handleImageClick = useCallback(async (s3Key: string, filename: string) => {
		try {
			const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/+$/, '');
			const response = await fetch(`${API_BASE_URL}/admin/beta-feedback/${feedbackId}/attachments/presign-get`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json'
				},
				credentials: 'include',
				body: JSON.stringify({ s3Key })
			});

			if (!response.ok) {
				throw new Error('Failed to get download URL');
			}

			const { presignedUrl } = await response.json();
			setImageDialog({ open: true, src: presignedUrl, title: filename });
		} catch (err: any) {
			console.error('Failed to get image URL:', err);
			alert('Failed to load image');
		}
	}, [feedbackId, currentBusiness?.id]);

	if (error) {
		return (
			<Box sx={{ p: 3 }}>
				<Alert severity="error">
					Failed to load feedback: {error.message}
				</Alert>
			</Box>
		);
	}

	if (isLoading || !feedback) {
		return (
			<Box sx={{ p: 3 }}>
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
					<Skeleton variant="text" width={300} height={40} />
					<IconButton onClick={onClose}>
						<CloseIcon />
					</IconButton>
				</Box>
				<Skeleton variant="rectangular" width="100%" height={200} />
			</Box>
		);
	}

	const statusColors: Record<string, any> = {
		'UNRESOLVED': 'error',
		'TRIAGED': 'warning',
		'IN_PROGRESS': 'info',
		'DONE': 'success',
		'WONTFIX': 'default',
		'DUPLICATE': 'default',
		'NEEDS_INFO': 'warning'
	};

	return (
		<Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
			{/* Header */}
			<Box sx={{ 
				p: 2, 
				borderBottom: 1, 
				borderColor: 'divider',
				display: 'flex', 
				justifyContent: 'space-between', 
				alignItems: 'center',
				backgroundColor: 'background.paper'
			}}>
				<Box>
					<Typography variant="h6" component="h2" noWrap sx={{ maxWidth: 400 }}>
						{feedback.title}
					</Typography>
					<Stack direction="row" spacing={1} sx={{ mt: 1 }}>
						<Chip 
							label={feedback.type} 
							color={feedback.type === 'BUG' ? 'error' : 'info'} 
							size="small" 
						/>
						<Chip 
							label={feedback.status.replace('_', ' ')} 
							color={statusColors[feedback.status]} 
							size="small" 
						/>
						{feedback.severity && (
							<Chip label={feedback.severity} size="small" />
						)}
					</Stack>
				</Box>
				<Stack direction="row" spacing={1}>
					<Button
						variant={isEditing ? "outlined" : "contained"}
						color={isEditing ? "inherit" : "primary"}
						startIcon={isEditing ? <CloseIcon /> : <AssignmentIcon />}
						onClick={handleEditToggle}
						disabled={isSaving}
					>
						{isEditing ? 'Cancel' : 'Edit'}
					</Button>
					{isEditing && (
						<Button
							variant="contained"
							color="primary"
							startIcon={<SaveIcon />}
							onClick={handleSave}
							disabled={isSaving}
						>
							{isSaving ? 'Saving...' : 'Save'}
						</Button>
					)}
					<IconButton onClick={onClose}>
						<CloseIcon />
					</IconButton>
				</Stack>
			</Box>

			{/* Tabs */}
			<Box sx={{ borderBottom: 1, borderColor: 'divider', backgroundColor: 'background.paper' }}>
				<Tabs value={activeTab} onChange={handleTabChange}>
					<Tab label="Overview" />
					<Tab label="Steps" />
					<Tab label={`Attachments (${feedback.attachments?.length || 0})`} />
					<Tab label={`Comments (${feedback.comments?.length || 0})`} />
					<Tab label="Environment" />
				</Tabs>
			</Box>

			{/* Tab Content */}
			<Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
				{/* Overview Tab */}
				<TabPanel value={activeTab} index={0}>
					<Grid container spacing={3}>
						<Grid sx={{ width: { xs: '100%', md: '50%' } }}>
							<Card>
								<CardContent>
									<Typography variant="h6" gutterBottom>
										Details
									</Typography>
									
									{isEditing ? (
										<Stack spacing={2}>
											<FormControl fullWidth>
												<InputLabel>Status</InputLabel>
												<Select
													value={editValues.status}
													onChange={(e) => setEditValues((prev: any) => ({ ...prev, status: e.target.value }))}
													label="Status"
												>
													{getAllowedStatusOptions(feedback.status).map((s) => (
														<MenuItem key={s} value={s}>{STATUS_LABELS[s] || s}</MenuItem>
													))}
												</Select>
											</FormControl>
											
											{feedback.type === 'BUG' && (
												<FormControl fullWidth>
													<InputLabel>Severity</InputLabel>
													<Select
														value={editValues.severity}
														onChange={(e) => setEditValues((prev: any) => ({ ...prev, severity: e.target.value }))}
														label="Severity"
													>
														<MenuItem value="P0">P0 (Critical)</MenuItem>
														<MenuItem value="P1">P1 (High)</MenuItem>
														<MenuItem value="P2">P2 (Medium)</MenuItem>
														<MenuItem value="P3">P3 (Low)</MenuItem>
													</Select>
												</FormControl>
											)}
											
											<FormControl fullWidth>
												<InputLabel>Category</InputLabel>
												<Select
													value={editValues.category}
													onChange={(e) => setEditValues((prev: any) => ({ ...prev, category: e.target.value }))}
													label="Category"
												>
													<MenuItem value="UI/UX">UI/UX</MenuItem>
													<MenuItem value="Functional">Functional</MenuItem>
													<MenuItem value="Performance">Performance</MenuItem>
													<MenuItem value="Data/Sync">Data/Sync</MenuItem>
													<MenuItem value="Email/Notifications">Email/Notifications</MenuItem>
													<MenuItem value="Auth/Access">Auth/Access</MenuItem>
													<MenuItem value="Billing/Payments">Billing/Payments</MenuItem>
													<MenuItem value="Other">Other</MenuItem>
												</Select>
											</FormControl>
											
											<TextField
												fullWidth
												label="External Issue URL"
												value={editValues.external_issue_url}
												onChange={(e) => setEditValues((prev: any) => ({ ...prev, external_issue_url: e.target.value }))}
												placeholder="https://github.com/..."
											/>
											
											<TextField
												fullWidth
												label="Resolution Summary"
												multiline
												rows={3}
												value={editValues.resolution_summary}
												onChange={(e) => setEditValues((prev: any) => ({ ...prev, resolution_summary: e.target.value }))}
												placeholder="Description of how this was resolved..."
												required={TERMINAL_STATUSES.has(String(editValues.status))}
												error={TERMINAL_STATUSES.has(String(editValues.status)) && !String(editValues.resolution_summary || '').trim()}
												helperText={TERMINAL_STATUSES.has(String(editValues.status)) && !String(editValues.resolution_summary || '').trim() ? 'Resolution summary is required for Done/Won\'t Fix/Duplicate' : undefined}
											/>
											
											{editValues.status === 'DUPLICATE' && (
												<TextField
													fullWidth
													label="Duplicate Of (Feedback ID)"
													value={editValues.duplicate_of}
													onChange={(e) => setEditValues((prev: any) => ({ ...prev, duplicate_of: e.target.value }))}
													placeholder="UUID of duplicate feedback"
													required
													error={!editValues.duplicate_of}
													helperText={!editValues.duplicate_of ? 'Required when marking as Duplicate' : undefined}
												/>
											)}
										</Stack>
									) : (
										<Stack spacing={2}>
											<Box>
												<Typography variant="body2" color="textSecondary">Type</Typography>
												<Typography>{feedback.type}</Typography>
											</Box>
											
											{feedback.category && (
												<Box>
													<Typography variant="body2" color="textSecondary">Category</Typography>
													<Typography>{feedback.category}</Typography>
												</Box>
											)}
											
											{feedback.severity && (
												<Box>
													<Typography variant="body2" color="textSecondary">Severity</Typography>
													<Typography>{feedback.severity}</Typography>
												</Box>
											)}
											
											<Box>
												<Typography variant="body2" color="textSecondary">Reporter</Typography>
												<Typography>{feedback.reporter_email}</Typography>
											</Box>
											
											{(feedback.business_name || feedback.business_id) && (
												<Box>
													<Typography variant="body2" color="textSecondary">Business</Typography>
													<Typography>{feedback.business_name || feedback.business_id}</Typography>
												</Box>
											)}
											
											<Box>
												<Typography variant="body2" color="textSecondary">Created</Typography>
												<Typography>{new Date(feedback.created_at).toLocaleString()}</Typography>
											</Box>
											
											{feedback.assigned_email && (
												<Box>
													<Typography variant="body2" color="textSecondary">Assigned To</Typography>
													<Typography>{feedback.assigned_email}</Typography>
												</Box>
											)}
											
											{feedback.external_issue_url && (
												<Box>
													<Typography variant="body2" color="textSecondary">External Issue</Typography>
													<Typography 
														component="a" 
														href={feedback.external_issue_url} 
														target="_blank" 
														sx={{ color: 'primary.main' }}
													>
														{feedback.external_issue_url}
													</Typography>
												</Box>
											)}
											
											{feedback.resolution_summary && (
												<Box>
													<Typography variant="body2" color="textSecondary">Resolution</Typography>
													<Typography>{feedback.resolution_summary}</Typography>
												</Box>
											)}
																						</Stack>
											)}
										</CardContent>
							</Card>
						</Grid>
						
						<Grid sx={{ width: { xs: '100%', md: '50%' } }}>
							<Card>
								<CardContent>
									<Typography variant="h6" gutterBottom>
										Description
									</Typography>
									
									{feedback.type === 'BUG' ? (
										<Stack spacing={2}>
											{feedback.expected && (
												<Box>
													<Typography variant="body2" color="textSecondary" gutterBottom>
														Expected Behavior
													</Typography>
													<Typography>{feedback.expected}</Typography>
												</Box>
											)}
											
											{feedback.actual && (
												<Box>
													<Typography variant="body2" color="textSecondary" gutterBottom>
														Actual Behavior
													</Typography>
													<Typography>{feedback.actual}</Typography>
												</Box>
											)}
											
											{feedback.context && (
												<Box>
													<Typography variant="body2" color="textSecondary" gutterBottom>
														Additional Context
													</Typography>
													<Typography>{feedback.context}</Typography>
												</Box>
											)}
										</Stack>
									) : (
										<Stack spacing={2}>
											{feedback.suggestion && (
												<Box>
													<Typography variant="body2" color="textSecondary" gutterBottom>
														Suggestion
													</Typography>
													<Typography>{feedback.suggestion}</Typography>
												</Box>
											)}
											
											{feedback.impact && (
												<Box>
													<Typography variant="body2" color="textSecondary" gutterBottom>
														Impact
													</Typography>
													<Typography>{feedback.impact}</Typography>
												</Box>
											)}
											
											{feedback.context && (
												<Box>
													<Typography variant="body2" color="textSecondary" gutterBottom>
														Additional Context
													</Typography>
													<Typography>{feedback.context}</Typography>
												</Box>
											)}
										</Stack>
									)}
								</CardContent>
							</Card>
						</Grid>
					</Grid>
				</TabPanel>
				
				{/* Steps Tab */}
				<TabPanel value={activeTab} index={1}>
					{feedback.type === 'BUG' && feedback.steps ? (
						<Card>
							<CardContent>
								<Typography variant="h6" gutterBottom>
									Steps to Reproduce
								</Typography>
								<List>
																						{normalizeSteps(feedback.steps).map((step: string, index: number) => (
														<ListItem key={index}>
															<ListItemText
																primary={`${index + 1}. ${step}`}
															/>
														</ListItem>
													))}
								</List>
							</CardContent>
						</Card>
					) : (
						<Alert severity="info">
							Steps to reproduce are only available for bug reports.
						</Alert>
					)}
				</TabPanel>
				
				{/* Attachments Tab */}
				<TabPanel value={activeTab} index={2}>
					{feedback.attachments && feedback.attachments.length > 0 ? (
						<Grid container spacing={2}>
							{feedback.attachments.map((attachment: any) => (
								<Grid sx={{ width: { xs: '100%', sm: '50%', md: '33.333%' } }} key={attachment.id}>
									<Card 
										sx={{ cursor: 'pointer', '&:hover': { boxShadow: 3 } }}
										onClick={() => handleImageClick(attachment.s3_key, `attachment-${attachment.id}`)}
									>
										<CardContent>
											<Stack direction="row" spacing={2} alignItems="center">
												<ImageIcon color="primary" />
												<Box sx={{ flex: 1, minWidth: 0 }}>
													<Typography variant="body2" noWrap>
														{attachment.mime_type}
													</Typography>
													<Typography variant="caption" color="textSecondary">
														{attachment.width} × {attachment.height} • {Math.round(attachment.size_bytes / 1024)}KB
													</Typography>
												</Box>
												<IconButton size="small">
													<DownloadIcon />
												</IconButton>
											</Stack>
										</CardContent>
									</Card>
								</Grid>
							))}
						</Grid>
					) : (
						<Alert severity="info">
							No attachments for this feedback.
						</Alert>
					)}
				</TabPanel>
				
				{/* Comments Tab */}
				<TabPanel value={activeTab} index={3}>
					<Stack spacing={2}>
						{feedback.comments && feedback.comments.length > 0 ? (
							feedback.comments.map((comment: any) => (
								<Paper key={comment.id} sx={{ p: 2 }}>
									<Stack direction="row" spacing={2}>
										<Avatar sx={{ width: 32, height: 32 }}>
											{comment.author_email?.charAt(0).toUpperCase()}
										</Avatar>
										<Box sx={{ flex: 1 }}>
											<Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
												<Typography variant="body2" fontWeight="medium">
													{comment.author_email}
												</Typography>
												<Typography variant="caption" color="textSecondary">
													{new Date(comment.created_at).toLocaleString()}
												</Typography>
											</Stack>
											<Typography variant="body2">
												{comment.body}
											</Typography>
										</Box>
									</Stack>
								</Paper>
							))
						) : (
							<Alert severity="info">
								No comments yet.
							</Alert>
						)}
						
						<Card>
							<CardContent>
								<Typography variant="h6" gutterBottom>
									Add Comment
								</Typography>
								<Stack spacing={2}>
									<TextField
										fullWidth
										multiline
										rows={3}
										placeholder="Enter your comment..."
										value={newComment}
										onChange={(e) => setNewComment(e.target.value)}
									/>
									<Button
										variant="contained"
										startIcon={<CommentIcon />}
										onClick={handleAddComment}
										disabled={!newComment.trim() || isAddingComment}
										sx={{ alignSelf: 'flex-start' }}
									>
										{isAddingComment ? 'Adding...' : 'Add Comment'}
									</Button>
								</Stack>
							</CardContent>
						</Card>
					</Stack>
				</TabPanel>
				
				{/* Environment Tab */}
				<TabPanel value={activeTab} index={4}>
					<Card>
						<CardContent>
							<Typography variant="h6" gutterBottom>
								Environment Information
							</Typography>
							{feedback.environment && (
								<Stack spacing={2}>
									<Box>
										<Typography variant="body2" color="textSecondary">URL</Typography>
										<Typography fontFamily="monospace">{feedback.environment.url}</Typography>
									</Box>
									<Box>
										<Typography variant="body2" color="textSecondary">User Agent</Typography>
										<Typography fontFamily="monospace" sx={{ wordBreak: 'break-all' }}>
											{feedback.environment.userAgent}
										</Typography>
									</Box>
									<Box>
										<Typography variant="body2" color="textSecondary">Viewport</Typography>
										<Typography fontFamily="monospace">
											{feedback.environment.viewport?.width} × {feedback.environment.viewport?.height}
										</Typography>
									</Box>
									{feedback.environment.appVersion && (
										<Box>
											<Typography variant="body2" color="textSecondary">App Version</Typography>
											<Typography fontFamily="monospace">{feedback.environment.appVersion}</Typography>
										</Box>
									)}
							</Stack>
						)}
						</CardContent>
					</Card>
				</TabPanel>
			</Box>
			
			{/* Image Dialog */}
			<Dialog
				open={imageDialog.open}
				onClose={() => setImageDialog({ open: false, src: '', title: '' })}
				maxWidth="lg"
				fullWidth
			>
				<DialogTitle>
					{imageDialog.title}
					<IconButton
						sx={{ position: 'absolute', right: 8, top: 8 }}
						onClick={() => setImageDialog({ open: false, src: '', title: '' })}
					>
						<CloseIcon />
					</IconButton>
				</DialogTitle>
				<DialogContent>
					{imageDialog.src && (
						<Box sx={{ textAlign: 'center' }}>
							<img
								src={imageDialog.src}
								alt={imageDialog.title}
								style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
							/>
						</Box>
					)}
				</DialogContent>
			</Dialog>
		</Box>
	);
}; 